import React from 'react'
import useSWRInfinite from 'swr/infinite'

import { IMMUTABLE_SWR_CONFIG } from '../../providers/ApiProvider'

const useInfiniteFetch = (url, initialSize = 25) => {
  const [pageSize, setPageSize] = React.useState(initialSize)

  const { data, error, mutate, size, setSize } = useSWRInfinite(index => {
    const urlVal = typeof url === 'function' ? url() : url
    if (!urlVal) {
      return null
    }

    return `${urlVal}&page=${index}&per_page=${pageSize}`
  }, IMMUTABLE_SWR_CONFIG)

  const clear = React.useCallback(() => {
    setSize(0)
  }, [setSize])

  const loadMore = React.useCallback(() => {
    setSize(old => old + 1)
  }, [setSize])

  const tasks = React.useMemo(() => (data ? [].concat(...data) : []), [data])
  const isLoadingInitialData = React.useMemo(
    () => !data && !error,
    [data, error]
  )
  const isLoadingMore = React.useMemo(
    () =>
      isLoadingInitialData ||
      (size > 0 && data && typeof data[size - 1] === 'undefined'),
    [isLoadingInitialData, size, data]
  )
  const isEmpty = React.useMemo(
    () => data?.[0]?.pagination?.total === 0,
    [data]
  )
  const totalData = React.useMemo(
    () => data && data[0].pagination.total,
    [data]
  )
  const currentData = React.useMemo(
    () => data && tasks.flatMap(task => task.data).length,
    [data, tasks]
  )
  const reachedEnd = React.useMemo(
    () => isEmpty || currentData >= totalData,
    [isEmpty, currentData, totalData]
  )
  const fullPagination = React.useMemo(() => {
    if (!data || isEmpty) {
      return { page: 0, per_page: pageSize, total: 0, urlFunc: () => '' }
    }

    const last = data[data.length - 1]
    return last.pagination
  }, [data, isEmpty, pageSize])
  const flatData = React.useMemo(() => {
    if (!tasks) {
      return []
    }

    return tasks.flatMap(tasks => tasks.data)
  }, [tasks])

  return {
    data: tasks,
    isLoadingInitialData,
    isLoadingMore,
    reachedEnd,
    isEmpty,
    currentData,
    totalData,
    fullPagination,
    setPageSize,
    mutate,
    clear,
    loadMore,
    loaded: !isLoadingInitialData,

    // Convenience values
    flatData,
    isLoading: isLoadingInitialData || isLoadingMore,
    canLoadMore: !reachedEnd && !(isLoadingInitialData || isLoadingMore)
  }
}

export default useInfiniteFetch
